<template>
  <div class="premium-locked-page">
    <v-card variant="flat" class="premium-locked-page__hero">
      <!-- Panou vizual: glyph-ul funcționalității pe suprafață tertiary-soft.
           Decorativ, deci ascuns pentru screen readers. -->
      <aside class="premium-locked-page__visual" aria-hidden="true">
        <span class="premium-locked-page__halo premium-locked-page__halo--outer"></span>
        <span class="premium-locked-page__halo premium-locked-page__halo--inner"></span>
        <span class="premium-locked-page__glyph">
          <v-icon size="44">{{ icon }}</v-icon>
        </span>
        <span class="premium-locked-page__lock">
          <v-icon size="15">mdi-lock-outline</v-icon>
        </span>
      </aside>

      <div class="premium-locked-page__content">
        <p class="premium-locked-page__eyebrow">
          <v-icon size="13">mdi-star-four-points-outline</v-icon>
          <span>{{ $t("premium.locked.eyebrow") }}</span>
        </p>

        <h1 class="premium-locked-page__title">{{ $t(`${translationBase}.title`) }}</h1>
        <p class="premium-locked-page__description">{{ $t(`${translationBase}.description`) }}</p>

        <section class="premium-locked-page__benefits" :aria-label="$t('premium.locked.includedTitle')">
          <h2 class="premium-locked-page__benefits-title">
            {{ $t("premium.locked.includedTitle") }}
          </h2>
          <ul class="premium-locked-page__benefits-grid">
            <li v-for="benefitKey in benefitKeys" :key="benefitKey">
              <v-icon size="17" class="premium-locked-page__benefit-icon">
                mdi-check-circle-outline
              </v-icon>
              <span>{{ $t(`${translationBase}.benefits.${benefitKey}`) }}</span>
            </li>
          </ul>
        </section>

        <!-- Callout cald: token rezervat prin `docs/design.md` pentru
             onboarding/help surfaces, nu pentru acțiuni repetitive. -->
        <p class="premium-locked-page__note">
          <v-icon size="17" class="premium-locked-page__note-icon">mdi-handshake-outline</v-icon>
          <span>{{ $t(`${translationBase}.note`) }}</span>
        </p>

        <div class="premium-locked-page__actions">
          <v-btn
            color="primary"
            variant="flat"
            size="large"
            class="premium-locked-page__cta"
            :href="contactUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-icon start>mdi-message-text-outline</v-icon>
            {{ $t("premium.locked.cta") }}
          </v-btn>
          <v-btn variant="text" size="large" to="/ncrm">
            <v-icon start>mdi-arrow-left</v-icon>
            {{ $t("premium.locked.backToCrm") }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import {
  PREMIUM_FEATURE_SLUGS,
  resolvePremiumContactUrl,
} from "@/Utile/premiumAccess.mjs";

const FEATURE_ICONS = Object.freeze({
  aiWorkspace: "mdi-robot-outline",
  dataIntegrations: "mdi-database-sync-outline",
  emailMarketing: "mdi-email-newsletter",
  workflows: "mdi-sitemap-outline",
});

export default {
  name: "PremiumLockedPage",
  props: {
    featureKey: { type: String, required: true },
  },
  computed: {
    featureSlug() {
      return PREMIUM_FEATURE_SLUGS[this.featureKey] || "workflows";
    },
    translationBase() {
      return `premium.features.${this.featureSlug}`;
    },
    icon() {
      return FEATURE_ICONS[this.featureSlug] || "mdi-lock-outline";
    },
    benefitKeys() {
      return ["one", "two", "three"];
    },
    contactUrl() {
      return resolvePremiumContactUrl(this.$i18n?.locale);
    },
  },
};
</script>

<style scoped>
.premium-locked-page {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 100%;
  padding: 32px 24px;
  background: var(--app-page-background);
}

.premium-locked-page__hero {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  width: 100%;
  max-width: 1060px;
  margin-top: 24px;
  overflow: hidden;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* ---------- Panou vizual ---------- */

.premium-locked-page__visual {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 100%;
  padding: 40px 24px;
  /* Halourile sunt mai late decât panoul pe ecrane mici; fără clip ar depăși
     în zona de conținut. */
  overflow: hidden;
  background: var(--app-tertiary-soft-bg);
  border-right: 1px solid var(--app-tertiary-soft-border);
}

.premium-locked-page__halo {
  position: absolute;
  border-radius: var(--app-radius-pill);
  border: 1px solid var(--app-tertiary-soft-border);
}

.premium-locked-page__halo--outer {
  width: 210px;
  height: 210px;
  opacity: 0.75;
}

.premium-locked-page__halo--inner {
  width: 148px;
  height: 148px;
}

.premium-locked-page__glyph {
  position: relative;
  display: grid;
  place-items: center;
  width: 92px;
  height: 92px;
  color: var(--app-tertiary-soft-text);
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--app-tertiary-soft-border);
  border-radius: var(--app-radius-base);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

/* Lacătul comunică starea „blocat" prin formă, nu doar prin culoare. */
.premium-locked-page__lock {
  position: absolute;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  transform: translate(46px, 34px);
  color: rgb(var(--v-theme-surface));
  background: rgb(var(--v-theme-primary));
  border: 2px solid rgb(var(--v-theme-surface));
  border-radius: var(--app-radius-pill);
}

/* ---------- Conținut ---------- */

.premium-locked-page__content {
  /* Alinierea se declară explicit, nu se moștenește din shell-ul aplicației
     (`docs/design.md`: standardul global este `text-left`). */
  padding: 40px;
  text-align: left;
}

.premium-locked-page__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 4px 11px;
  color: var(--app-secondary-soft-text);
  background: var(--app-secondary-soft-bg);
  border: 1px solid var(--app-secondary-soft-border);
  border-radius: var(--app-radius-pill);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.premium-locked-page__title {
  max-width: 640px;
  margin: 18px 0 0;
  color: var(--app-text-strong);
  /* Scara din docs/design.md: H1 28–32px semibold. */
  font-size: clamp(1.75rem, 2.6vw, 2rem);
  font-weight: 600;
  line-height: 1.22;
}

.premium-locked-page__description {
  max-width: 640px;
  margin: 14px 0 0;
  color: var(--app-text-default);
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.86;
}

/* ---------- Beneficii ---------- */

.premium-locked-page__benefits {
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid var(--ncrm-color-border-subtle);
}

.premium-locked-page__benefits-title {
  margin: 0 0 14px;
  color: var(--app-quaternary-soft-text);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.premium-locked-page__benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.premium-locked-page__benefits-grid li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 13px 14px;
  color: var(--app-text-default);
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
  font-size: 0.89rem;
  line-height: 1.45;
}

.premium-locked-page__benefit-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--app-tertiary-soft-text);
}

/* ---------- Callout ---------- */

.premium-locked-page__note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 22px 0 0;
  padding: 13px 16px;
  color: var(--app-text-default);
  background: var(--app-warm-highlight-soft-bg);
  border: 1px solid var(--app-warm-highlight-soft-border);
  border-radius: var(--app-radius-base);
  font-size: 0.9rem;
  line-height: 1.5;
}

.premium-locked-page__note-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--app-secondary-soft-text);
}

/* ---------- Acțiuni ---------- */

.premium-locked-page__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 26px;
}

.premium-locked-page__cta {
  letter-spacing: 0;
}

/* ---------- Responsive ---------- */

@media (max-width: 960px) {
  .premium-locked-page__hero {
    grid-template-columns: 1fr;
  }

  .premium-locked-page__visual {
    min-height: 0;
    padding: 32px 24px;
    border-right: none;
    border-bottom: 1px solid var(--app-tertiary-soft-border);
  }

  /* Dimensionate să încapă în înălțimea redusă a panoului pe mobil, ca cercul
     să nu apară retezat de clip. */
  .premium-locked-page__halo--outer {
    width: 132px;
    height: 132px;
  }

  .premium-locked-page__halo--inner {
    width: 100px;
    height: 100px;
  }

  .premium-locked-page__glyph {
    width: 76px;
    height: 76px;
  }

  .premium-locked-page__lock {
    transform: translate(38px, 28px);
  }

  .premium-locked-page__benefits-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .premium-locked-page {
    padding: 16px;
  }

  .premium-locked-page__hero {
    margin-top: 0;
  }

  .premium-locked-page__content {
    padding: 26px 22px;
  }

  .premium-locked-page__actions .v-btn {
    width: 100%;
  }
}
</style>
